<template>
  <!-- eslint-disable max-len -->
  <div class="panel panel-default">
    <div class="panel-heading">
      {{ $t('Two-factor authentication') }}

      <template v-if="multiFactors">
        <i
          :class="multiFactors.paginatorInfo.count ? 'tw-text-green-500' : 'tw-text-gray-700'"
          class="zmdi zmdi-shield-check pull-right f-20 animated pulse"
        />
      </template>
    </div>

    <div class="panel-body">
      <!-- eslint-disable-next-line vue/no-v-for-template-key -- Baseline eslint ignore please fix if working on this-->
      <template v-for="(formattedError, i) in graphQLErrors" :key="i + '_' + formattedError.key">
        <WdsAlert>{{ formattedError.key }} {{ formattedError.message }}</WdsAlert>
      </template>

      <div
        v-for="error in errors"
        :key="error.message"
        class="alert alert-warning text-center"
        role="alert"
      >
        {{ error.message }}
      </div>

      <div>
        <p class="c-gray f-12">
          {{
            $t(
              'Two-factor authentication helps keep your account safe by adding a security process that requires two forms of user ID to log in to Worksome.',
            )
          }}
          <br />
          {{
            $t('When you log in you will be required to enter a code that we will send to an app.')
          }}
          <br />
          {{
            $t(
              'There are two ways to do this: use an authenticator app, or enable SMS account verification.',
            )
          }}
        </p>
        <p />
        <hr />
      </div>
      <!-- Loading -->
      <div v-if="$apollo.loading">
        <div class="media skeleton p-5" style="background: none">
          <div class="col-md-6">
            <div class="row">
              <div class="skeleton-part-lg max-width-xl" />
            </div>
          </div>
          <div class="row col-md-offset-5 col-md-3">
            <div class="skeleton-part-lg skeleton-width-md" />
          </div>
        </div>
      </div>

      <!-- Error -->
      <template v-else-if="errors">
        {{ $t('An error occurred while fetching your Two-Factor Authentication settings.') }}
      </template>

      <!-- Result -->
      <template v-else-if="multiFactors">
        <div>
          <template v-if="multiFactors.paginatorInfo.count <= 0">
            <h4>
              {{ $t('Use an authenticator app for two-factor authentication') }}
            </h4>
            <p class="c-gray">
              {{ $t('Worksome integrates with mobile apps for two-factor authentication.') }}
            </p>
            <div class="row">
              <div class="col-sm-6">
                <a href="#" class="btn btn-default" @click="handleQr">
                  {{ $t('Use') }}
                  <span class="f-800">{{ $t('QR code') }}</span>
                </a>
                <p class="c-gray m-t-10 f-12">
                  {{
                    $t(
                      'Scan our QR code to add Worksome to your Google Authenticator, Microsoft Authenticator or a similar app.',
                    )
                  }}
                </p>
              </div>
            </div>
            <h4 class="m-t-20">
              {{ $t('Use SMS for two-factor authentication') }}
            </h4>
            <p class="c-gray">
              {{ $t('Worksome can also use an SMS message to provide two-factor authentication.') }}
            </p>
            <div v-if="hasPhoneNumber" class="row">
              <div class="col-sm-6">
                <a href="#" class="btn btn-default" @click="handleSms">
                  <i class="zmdi zmdi-plus-circle-o" />
                  {{ $t('Use') }}
                  <span class="f-800">SMS</span>
                </a>
              </div>
              <div class="col-sm-6" />
            </div>
            <div v-else class="c-gray">
              <p>
                {{ $t('SMS two-factor authentication requires a valid phone number.') }}
                <a :href="laravelNamedRoute('frontend.user.profile.edit-user')">
                  {{ $t('Click here to add one.') }}
                </a>
              </p>
            </div>
          </template>
          <template v-else>
            <h5>
              {{ $t('You have secured your account with a two-factor authentication.') }}
            </h5>
            <h6 class="text-gray p-t-5">
              <a href="#" class="btn btn-default" @click="handleModalRemoveOpen">
                {{ $t('Remove 2FA') }}
              </a>
            </h6>
            <div />
          </template>
        </div>
      </template>
    </div>

    <template v-if="showDeleteModal">
      <ModalConfirmSwal
        :header-text="$t('Do you really want to remove your Two-factor authentication?')"
        :text="
          $t('Confirm that you want to remove your current two-factor authentication (2FA) setup.')
        "
        :confirm-button-text="$t('Yes, delete')"
        :processing-text="$t('Now deleting')"
        @confirmed="handleDelete"
        @cancelled="handleModalDeleteCancel"
      />
    </template>

    <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
    <BaseModal
      :confirm-button-text="$t('Verify')"
      :is-open="showModal"
      :save-state="verifyState"
      :can-retry="true"
      :saving-text="$t('Verifying') + '...'"
      :failed-text="$t('Failed, try again')"
      @save="handleVerify"
      @cancel="handleModalCancel"
    >
      <template #header>
        <h4>
          {{ $t('Enable Authenticator App') }}
        </h4>
      </template>

      <div
        v-for="error in verifyErrors"
        :key="error"
        class="alert alert-warning m-t-10"
        role="alert"
      >
        {{ error }}
      </div>

      <template v-if="qrCode === false">
        <div class="form-group text-center p-20 m-t-20 bgm-lightgray">
          <label for="verifyField">
            {{ $t('Enter the verification code send to your phone') }}
          </label>
          <div class="row">
            <div class="col-sm-4 col-sm-offset-4">
              <input
                id="verifyField"
                v-model="verifyCode"
                type="text"
                pattern="\d*"
                class="form-control text-center fw"
                maxlength="7"
                minlength="7"
                placeholder="12 345 67"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-else-if="qrCode !== null">
        <div class="text-center p-5">
          {{ $t('Scan this code with an Authenticator App.') }}
        </div>
        <div class="text-center tw-flex tw-flex-col tw-items-center">
          <QrcodeVue :value="qrCode" :size="300"></QrcodeVue>
          <div class="tw-mt-2">
            Code:
            <span class="tw-font-bold">{{ totpSecret }}</span>
          </div>
        </div>
        <div class="form-group text-center p-20 bgm-lightgray">
          <label for="verifyFieldQr">
            {{ $t('Enter the verification code generated by your mobile application.') }}
          </label>
          <div class="row">
            <div class="col-sm-4 col-sm-offset-4">
              <input
                id="verifyFieldQr"
                v-model="verifyCode"
                type="text"
                pattern="\d*"
                class="form-control c-black text-center fw"
                maxlength="6"
                minlength="6"
                placeholder="123 456"
              />
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="media skeleton p-5">
          <div class="row">
            <div class="col-sm-6 col-sm-offset-3">
              <div class="skeleton-part-lg fw" />
            </div>
          </div>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import QrcodeVue from 'qrcode.vue'
import { WdsAlert } from '@worksome/wds-components'
import ModalConfirmSwal from '@/components/ModalConfirmSwal.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import BaseModal from '@/components/BaseModal.vue'

export default {
  components: { BaseModal, WdsAlert, ModalConfirmSwal, QrcodeVue },
  data() {
    return {
      graphQLErrors: [],
      showModal: false,
      qrCode: null,
      totpSecret: null,
      mfaId: null,
      verifyCode: null,
      verifyErrors: null,
      verifyState: 'fresh',
      errors: null,
      showDeleteModal: false,
      hasPhoneNumber: !!currentUser.phone,
    }
  },
  apollo: {
    $client: 'publicApi',
    multiFactors: {
      query: gql`
        query twoFactorAuthenticationAppStatus {
          multiFactors(statuses: [APPROVED]) {
            paginatorInfo {
              count
            }
            data {
              ... on HasMultiFactorMetadata {
                id
              }
            }
          }
        }
      `,
      error(error) {
        this.handleAdd2FAError(error)
      },
    },
  },
  methods: {
    handleSms() {
      this.showModal = true
      this.qrCode = null
      this.totpSecret = null
      this.verifyErrors = null
      this.verifyState = 'fresh'
      this.$apollo
        .mutate({
          mutation: this.$options.addSms,
          variables: {
            name: 'SMS Multi-Factor | Platform',
          },
        })
        .then((result) => {
          this.qrCode = false
          this.mfaId = result.data.create.multiFactor.id
        })
        .catch((error) => {
          this.handleAdd2FAError(error)
        })
    },
    handleQr() {
      this.qrCode = null
      this.totpSecret = null
      this.showModal = true
      this.verifyErrors = null
      this.verifyState = 'fresh'
      this.$apollo
        .mutate({
          mutation: this.$options.addTotp,
          variables: {
            name: 'TOTP (QR) Multi-Factor | Platform',
          },
        })
        .then((result) => {
          this.qrCode = result.data.create.qrCode
          this.totpSecret = result.data.create.secret
          this.mfaId = result.data.create.multiFactor.id
        })
        .catch((error) => this.handleAdd2FAError(error))
    },
    handleAdd2FAError(errors) {
      this.graphQLErrors = errors.graphQLErrors

      this.showModal = false
    },
    handleModalCancel() {
      this.showModal = false
    },
    handleModalDeleteCancel() {
      this.showDeleteModal = false
    },
    handleVerify() {
      this.verifyErrors = null
      this.verifyState = 'saving'
      this.$apollo
        .mutate({
          mutation: this.qrCode !== false ? this.$options.verifyTotp : this.$options.verifySms,
          variables: {
            token: this.verifyCode,
            id: this.mfaId,
          },
        })
        .then((result) => {
          if (result.data.verify.status === 'APPROVED') {
            this.verifyState = 'saved'
            this.showModal = false
            this.verifyCode = ''
            this.$apollo.queries.multiFactors.refetch()

            this.refreshAuthUserData()
          } else {
            this.verifyState = 'failed'
            this.verifyErrors = ['Invalid code entered']
          }
        })
        .catch((errors) => {
          this.verifyErrors = errors.graphQLErrors.flatMap((error) => {
            const validationErrors = error.extensions.validation ?? {}
            return Object.keys(validationErrors).flatMap((key) => validationErrors[key])
          })

          this.verifyState = 'failed'
        })
    },
    handleModalRemoveOpen() {
      this.showDeleteModal = true
    },
    handleDelete() {
      this.$apollo
        .mutate({
          mutation: this.$options.deleteMfa,
          variables: {
            id: this.multiFactors.data[0].id,
          },
        })
        .then(() => {
          this.showDeleteModal = false
          this.$apollo.queries.multiFactors.refetch()

          this.refreshAuthUserData()
        })
        .catch((error) => {
          this.showDeleteModal = false
          this.errors = error.graphQLErrors
        })
    },
    refreshAuthUserData() {
      fetch(laravelNamedRoute('auth.user.refresh'), {
        method: 'PATCH',
        headers: {
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
      }).then(() => location.reload())
    },
    laravelNamedRoute,
  },
  addTotp: gql`
    mutation create($name: String!) {
      create: createTotpMultiFactor(input: { name: $name }) {
        secret
        qrCode
        multiFactor {
          id
        }
      }
    }
  `,
  addSms: gql`
    mutation create($name: String!) {
      create: createSmsMultiFactor(input: { name: $name }) {
        multiFactor {
          id
        }
      }
    }
  `,

  verifyTotp: gql`
    mutation verify($token: String!, $id: ID!) {
      verify: verifyTotpMultiFactor(input: { id: $id, code: $token }) {
        status
      }
    }
  `,
  verifySms: gql`
    mutation verify($token: String!, $id: ID!) {
      verify: verifySmsMultiFactor(input: { id: $id, code: $token }) {
        status
      }
    }
  `,
  deleteMfa: gql`
    mutation delete2FA($id: ID!) {
      removeMultiFactor(input: { id: $id }) {
        ... on HasMultiFactorMetadata {
          id
        }
      }
    }
  `,
}
</script>
